import clsx from "clsx";
import { truncate } from "lodash-es";
import { FC, useEffect, useRef, useState } from "react";

import { ReactionType } from "components/server-types";
import { t } from "i18n/i18n";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import { useAppSelector } from "store";
import { selectCurrentClassUserList, selectUserByClassUserId } from "store/selectors";
import { selectClassUserIdsVisibleInParticipantBar } from "store/selectors/virtual-class-users-sorting";
import { useListSelector } from "store/store-hooks";
import { REACTION_TO_BG_COLOR_CLASSES, REACTION_TO_ICON } from "utils/reactions";
import { getUserFullName } from "utils/user-utils";

const REACTION_DISPLAY_DURATION_MS = 5000;
const MAX_NAME_LENGTH = 30;
const FADE_IN_DURATION_MS = 150;
const EXPAND_DURATION_MS = 300;
const EXIT_PAUSE_BEFORE_FADE_MS = 200;

type NonNullReaction = NonNullable<ReactionType>;

type ReactionFeedState = {
  firstReaction: NonNullReaction;
  firstClassUserId: string;
  uniqueReactions: NonNullReaction[];
  totalCount: number;
};

// Animation phases:
// Enter: fade in w/ icon only → expand width → fade in text
// Exit: fade out text → collapse width → fade out
type AnimationPhase =
  | "entering-fade"
  | "entering-width"
  | "entering-complete"
  | "exiting-text"
  | "exiting-width"
  | "exiting-fade";

export const ReactionFeed: FC = () => {
  const classUsers = useListSelector(selectCurrentClassUserList, ["reaction"]);
  const [reactionFeedState, setReactionFeedState] = useState<ReactionFeedState | null>(null);
  const seenReactionsRef = useRef<Map<string, NonNullReaction>>(new Map());
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("entering-fade");
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSmOrLarger = useIsSmOrLarger();
  const classUserIdsVisibleInParticipantBar = useAppSelector(
    selectClassUserIdsVisibleInParticipantBar,
  );

  const firstReactorUser = useAppSelector((state) =>
    reactionFeedState ? selectUserByClassUserId(state, reactionFeedState.firstClassUserId) : null,
  );

  useEffect(() => {
    const currentReactions = new Map<string, NonNullReaction>();
    classUsers.forEach((cu) => {
      if (cu.reaction && !classUserIdsVisibleInParticipantBar.has(cu.id)) {
        currentReactions.set(cu.id, cu.reaction);
      }
    });

    // Find new reactions
    const newReactions: Array<{ classUserId: string; reaction: NonNullReaction }> = [];
    currentReactions.forEach((reaction, classUserId) => {
      const previousReaction = seenReactionsRef.current.get(classUserId);
      if (previousReaction !== reaction) {
        newReactions.push({ classUserId, reaction });
      }
    });

    if (newReactions.length === 0) {
      return;
    }

    // Clear any existing dismiss timer since we have new reactions
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }

    newReactions.forEach(({ classUserId, reaction }) => {
      seenReactionsRef.current.set(classUserId, reaction);
    });

    const isFirstReaction = reactionFeedState === null;

    setReactionFeedState((prev) => {
      const allReactions = Array.from(seenReactionsRef.current.values());
      const uniqueReactions = [...new Set(allReactions)];

      if (!prev) {
        // This is the first reaction in the group
        const first = newReactions[0];
        return {
          firstReaction: first.reaction,
          firstClassUserId: first.classUserId,
          uniqueReactions,
          totalCount: seenReactionsRef.current.size,
        };
      }

      // Add new reactions to the existing group
      return {
        ...prev,
        uniqueReactions,
        totalCount: seenReactionsRef.current.size,
      };
    });

    // Start enter animation sequence for first reaction
    if (isFirstReaction) {
      setAnimationPhase("entering-fade");

      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }

      animationTimerRef.current = setTimeout(() => {
        setAnimationPhase("entering-width");

        animationTimerRef.current = setTimeout(() => {
          setAnimationPhase("entering-complete");
          animationTimerRef.current = null;
        }, EXPAND_DURATION_MS);
      }, FADE_IN_DURATION_MS);
    }

    // Start exit animation sequence after display duration
    dismissTimerRef.current = setTimeout(() => {
      dismissTimerRef.current = null;

      setAnimationPhase("exiting-text");

      animationTimerRef.current = setTimeout(() => {
        setAnimationPhase("exiting-width");

        animationTimerRef.current = setTimeout(() => {
          setAnimationPhase("exiting-fade");

          animationTimerRef.current = setTimeout(() => {
            setReactionFeedState(null);
            seenReactionsRef.current.clear();
            setAnimationPhase("entering-fade");
            animationTimerRef.current = null;
          }, FADE_IN_DURATION_MS);
        }, EXPAND_DURATION_MS + EXIT_PAUSE_BEFORE_FADE_MS);
      }, FADE_IN_DURATION_MS);
    }, REACTION_DISPLAY_DURATION_MS);
    // Omit reactionFeedState: we only want to run when classUsers or participant bar
    // visibility changes, not when we clear state on dismiss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classUsers, classUserIdsVisibleInParticipantBar]);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);

  if (!reactionFeedState) {
    return null;
  }

  const bgColorClass = REACTION_TO_BG_COLOR_CLASSES[reactionFeedState.firstReaction];
  const isSingleReaction = reactionFeedState.totalCount === 1;

  // For a single reaction, show user's full name; otherwise show count
  let displayText: string;
  if (isSingleReaction) {
    const fullName = getUserFullName(firstReactorUser);
    displayText = truncate(fullName, { length: MAX_NAME_LENGTH });
  } else {
    displayText = t("reactions.count_reacted", { count: reactionFeedState.totalCount });
  }

  // Derived animation states
  const isExiting = animationPhase.startsWith("exiting");
  const showText = isSmOrLarger && animationPhase === "entering-complete";
  const showExpandedWidth =
    isSmOrLarger && (animationPhase === "entering-complete" || animationPhase === "exiting-text");
  const isVisible = animationPhase !== "entering-fade" && animationPhase !== "exiting-fade";

  return (
    <div
      className={clsx("absolute bottom-full left-4 z-50 mb-4 transition-opacity")}
      style={{
        opacity: isVisible ? 1 : 0,
        transitionDuration: `${FADE_IN_DURATION_MS}ms`,
      }}
    >
      <div
        className={clsx(
          "body-s flex items-center rounded-[2rem] py-2 pr-2 text-white shadow-2xl transition-[padding]",
          bgColorClass,
          showExpandedWidth ? "gap-2 pl-4" : "gap-0 pl-2",
        )}
        style={{ transitionDuration: `${EXPAND_DURATION_MS}ms` }}
      >
        {isSmOrLarger && (
          <div
            className="overflow-hidden whitespace-nowrap transition-[max-width]"
            style={{
              maxWidth: showExpandedWidth ? "500px" : "0px",
              transitionDuration: `${EXPAND_DURATION_MS}ms`,
            }}
          >
            <span
              className="transition-opacity"
              style={{
                opacity: showText ? 1 : 0,
                transitionDuration: `${FADE_IN_DURATION_MS}ms`,
                // On enter, delay until width expands; on exit, no delay
                transitionDelay: showText && !isExiting ? `${EXPAND_DURATION_MS}ms` : "0ms",
              }}
            >
              {displayText}
            </span>
          </div>
        )}

        <div className="flex items-center">
          {reactionFeedState.uniqueReactions.map((reaction, index) => {
            const ReactionIcon = REACTION_TO_ICON[reaction];
            return (
              <div
                className={clsx(index > 0 && "-ml-3")}
                key={reaction}
                style={{ zIndex: reactionFeedState.uniqueReactions.length - index }}
              >
                <ReactionIcon size="xxl" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
